import { Merchant } from "Components/Hunter/Merchants/Detail/Tabs/Merchant/Merchant";
import { Terminals } from "Components/Hunter/Merchants/Detail/Tabs/Terminals/Terminals";
import { SettlementsList } from "Components/Settlements/SettlementsList";
import { AppProducts } from "Components/Shared/Merchant/Products/AppProducts/AppProducts";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { MerchantDetailTransactionFees } from "Components/Transactions/UserTransactionFees/MerchantDetailTransactionFees";
import { SettlementDownloadSection } from "Components/Transactions/UserTransactionFees/SettlementDownloadSection";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { useSearchParams } from "Navigation/useSearchParams";
import { Resources, useResource } from "Translations/Resources";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { isSettlementUserRole } from "Types/settlements";
import { Box, Stack, Tab, Tabs } from "@mui/material";
import type { ReactNode } from "react";
import { PayPointsPageContent } from "Components/Merchant/PayPointsPageContent";
import { useIsCurrentUserCustomerOnboarding } from "Hooks/useIsCurrentUserCustomerOnboarding";
import { useIsCurrentUserAdmin } from "Hooks/useIsCurrentUserAdmin";

const COMPONENT_RESOURCES = Resources.Hunter.Merchants.Detail.Tabs;

type TabItem = {
  id: string;
  label: ReactNode;
  content: ReactNode;
  isHidden?: boolean;
};

type Props = {
  publicPartyID: string;
  merchantPublicID: string;
};

export const MerchantDetailPageContent = ({
  publicPartyID,
  merchantPublicID,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userRole = useCurrentUserRole();
  const isUserCustomerOnboarding = useIsCurrentUserCustomerOnboarding();
  const isAdmin = useIsCurrentUserAdmin();
  const { t } = useResource();

  const tabs: Array<TabItem> = [
    {
      id: "merchant",
      label: t(COMPONENT_RESOURCES.Merchant),
      content: (
        <Merchant
          publicPartyID={publicPartyID}
          merchantPublicID={merchantPublicID}
          isReadOnly={isUserCustomerOnboarding}
        />
      ),
    },
    {
      id: "products",
      label: t(COMPONENT_RESOURCES.Products),
      content: (
        <AppProducts
          merchantPublicID={merchantPublicID}
          variant={
            isAdmin ||
            isUserCustomerOnboarding ||
            userRole === AccessRoleCodes.Hunter ||
            userRole === AccessRoleCodes.HunterPlus
              ? "editable"
              : "readonly"
          }
        />
      ),
    },
    {
      id: "terminalContracts",
      label: t(COMPONENT_RESOURCES.TerminalContracts),
      content: (
        <Terminals
          merchantPublicID={merchantPublicID}
          isReadOnly={isUserCustomerOnboarding}
        />
      ),
    },
    {
      id: "paypoints",
      label: t(COMPONENT_RESOURCES.Paypoints),
      content: (
        <PayPointsPageContent
          merchantPublicID={merchantPublicID}
          isReadOnly={isUserCustomerOnboarding}
        />
      ),
    },
    ...(isSettlementUserRole(userRole)
      ? [
          {
            id: "transactions",
            label: t(COMPONENT_RESOURCES.Settlements),
            content: (
              <Stack>
                <MerchantDetailTransactionFees partyPublicID={publicPartyID} />
                <Box mt={2}>
                  <SettlementDownloadSection
                    partyPublicID={publicPartyID}
                    userRole={AccessRoleCodes.Merchant}
                    isReadOnly={isUserCustomerOnboarding}
                  />
                </Box>
                <Box mt={8}>
                  <SettlementsList
                    partyPublicID={publicPartyID}
                    userRole={AccessRoleCodes.Merchant}
                    isReadOnly={isUserCustomerOnboarding}
                  />
                </Box>
              </Stack>
            ),
          },
        ]
      : []),
  ];

  const activeTab = searchParams.get("activeTab") || tabs[0].id;

  return (
    <Box display="flex" alignItems="center" flexDirection="column">
      <Tabs
        value={activeTab}
        onChange={(_e, id: string) => setSearchParams({ activeTab: id })}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          width: "100%",
          "& .MuiTabs-scrollButtons.Mui-disabled": {
            opacity: 0.3,
          },
        }}
      >
        {tabs.map(({ label, id }) => (
          <Tab label={label} value={id} key={id} />
        ))}
      </Tabs>
      <Box mt="0.5rem" width="100%" maxWidth="600px">
        <PrimaryPaper>
          {tabs.map(({ content, id }) => (
            <div role="tabpanel" hidden={id !== activeTab} key={id}>
              {id === activeTab && content}
            </div>
          ))}
        </PrimaryPaper>
      </Box>
    </Box>
  );
};
